import { Injectable } from '@angular/core';
import { AccountFacade } from '@mode/accounts/contract';
import {
  ChartFormatHelpers,
  ChartTranslator,
  EncodingHelpers,
  FacetSizeHelpers,
  FieldFormatHelpers,
  SharedAxisHelpers,
  SwitchSchemaModel,
} from '@mode/charts/util-vegas';
import { ChartClientService } from '@mode/shared/data-access-webapp';
import {
  DEFAULT_CHART_FORMAT,
  FLAMINGO_RESERVED_REFS,
  deepCopy,
  getDefaultFieldFormat,
  isPresent,
  makeVizDesignerPlanMetadataId,
} from '@mode/shared/util-js';
import { Switch } from '@mode/shared/util-switch';
import { DataViewFacade, FlamingoFacade, PlanMetadataActions, SwitchViewActions } from '@mode/switch-views/contract';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import produce from 'immer';
import { combineLatest, of } from 'rxjs';
import { catchError, filter, first, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { ChartFacade } from '@mode/charts/contract';
import {
  generateColorMapping,
  isQuickChartColorChanged,
  isUpdateChartAction,
  isUpdateFormatOrColorAction,
  updateEncodingActions,
  updateFormatOrColorActions,
} from '../chart-helpers';
import { DomainAppState } from '../charts-domain.types';
import { withLatestSnapshot } from '../helpers';
import { getUpdatedTableFormat, requiresFormatUpdateFromEncodingChange } from '../table-state-helpers';
import { loadExploration } from '../temp.actions';
import * as ChartSwitchActions from './chart-switch.actions';
import * as ChartActions from './chart.actions';
import { createChartSuccess, loadChartsSuccess, loadGfVisualization } from './chart.actions';

import { ChartDrillDown } from '@mode/charts/util-vegas';
import { ChartTypes } from '@mode/shared/contract-common';
import {
  getDefaultColorPaletteForType,
  isColorPaletteCompatible,
  peekRedoStack,
  peekUndoStack,
} from '../chart-helpers';
@Injectable()
export class ChartEffects {
  loadExplorationDataView$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(loadExploration),
        tap((action) => this.dataViewFacade.loadExplorationDataView(action.dataView))
      ),
    { dispatch: false }
  );

  loadVisualizationDataView$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(loadGfVisualization),
        tap((action) => this.dataViewFacade.loadVisualizationDataView(action.dataView))
      ),
    { dispatch: false }
  );

  setRefOnLoad$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(loadChartsSuccess),
        tap((action) =>
          this.dataViewFacade.setSwitchViewRefs(
            action.payload.map((refs) => ({
              refId: refs.token,
              switchViewToken: refs.switch_view_token,
            }))
          )
        )
      ),
    { dispatch: false }
  );

  setRefOnCreate$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(createChartSuccess),
        tap((action) => this.dataViewFacade.setSwitchViewRef(action.payload.token, action.payload.switch_view_token))
      ),
    { dispatch: false }
  );

  /**
   * TODO: for all new SwitchView-based chart effects, need to handle the *Failure actions.
   * For now, all these failure actions will just result in a blocked UI
   *
   * Additionally, we will be listeninng for the unhandled failure actions to ensure bubbling
   * the error out into the browser. Once each of these are handled in the UI, they can
   * be removed from the temporary effect.
   */

  // Unhandled errors effect - This effect is temporary to ensure these errors get surfaced in the browser
  //   until these actions are explicitly handled in the UI. Once they are, they can be removed from this
  //   effect.
  unhandledErrors$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          ChartSwitchActions.createDefaultChartFailure,
          ChartSwitchActions.createVegasEncodingChartFailure,
          ChartSwitchActions.createNewChartFailure,
          ChartSwitchActions.createNewTableFailure,
          ChartSwitchActions.cloneChartFromBrowserFailure,
          ChartSwitchActions.cloneChartFromTabFailure,
          ChartSwitchActions.cloneChartFromBuilderFailure,
          ChartSwitchActions.cloneChartFromDesignerFailure
        ),
        map((action) => {
          console.error(action.error);
        })
      ),
    { dispatch: false }
  );

  // Create a new default (Line) chart
  createDefaultChart$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChartSwitchActions.createDefaultChart),
      withLatestFrom(this.accountFacade.defaultCategoricalPalette$, this.accountFacade.defaultCategoricalChartFormat$),
      switchMap(([action, palette, format]) => {
        return this.chartClient
          .createChart({
            colorPaletteToken: palette.token,
            switchViewToken: action.switchViewToken,
            chartType: 'table',
            encoding: EncodingHelpers.initEncoding('table').encoding,
            format,
          })
          .pipe(
            map((chart) => {
              return ChartSwitchActions.createDefaultChartSuccess({
                chart,
                switchViewToken: action.switchViewToken,
                queryToken: action.queryToken,
              });
            }),
            catchError((error) => {
              return of(
                ChartSwitchActions.createDefaultChartFailure({ error, switchViewToken: action.switchViewToken })
              );
            })
          );
      })
    )
  );

  // Create a new Vegas Encoding chart
  createVegasEncodingChart$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChartSwitchActions.createVegasEncodingChart),
      withLatestFrom(this.accountFacade.defaultCategoricalPalette$, this.accountFacade.defaultCategoricalChartFormat$),
      switchMap(([action, palette, format]) => {
        return this.chartClient
          .createChart({
            colorPaletteToken: palette.token,
            switchViewToken: action.switchViewToken,
            chartType: 'vegas',
            encoding: EncodingHelpers.initEncoding('vegas').encoding,
            format,
          })
          .pipe(
            map((chart) => {
              return ChartSwitchActions.createVegasEncodingChartSuccess({
                chart,
                switchViewToken: action.switchViewToken,
                queryToken: action.queryToken,
              });
            }),
            catchError((error) => {
              return of(
                ChartSwitchActions.createVegasEncodingChartFailure({ error, switchViewToken: action.switchViewToken })
              );
            })
          );
      })
    )
  );

  loadEditorViewsCharts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SwitchViewActions.loadEditorViewsSuccess, SwitchViewActions.refreshEditorViewsSuccess),
      switchMap((action) =>
        this.accountFacade.reportOwner$.pipe(
          first(),
          map((account) => ({ account, action }))
        )
      ),
      switchMap(({ action, account }) =>
        this.chartClient
          .getChartsFromViewsOnReport(account.username, action.reportToken)
          .pipe(map((charts) => ChartActions.loadChartsFromSwitchViewSuccess({ charts })))
      )
    )
  );

  createNewChart$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChartSwitchActions.createNewChart),
      withLatestFrom(
        this.accountFacade.defaultCategoricalPalette$,
        this.accountFacade.defaultCategoricalChartFormat$,
        this.accountFacade.defaultDivergentPalette$,
        this.accountFacade.defaultDivergentChartFormat$,
        this.accountFacade.defaultSequentialPalette$
      ),
      switchMap(([action, palette, defaultFormat, divergentPalette, divergentFormat, sequentialPalette]) => {
        let format = action.format ?? (action.chartType === 'bigValue' ? divergentFormat : defaultFormat);
        const encoding = action.encoding
          ? EncodingHelpers.loadEncoding(action.encoding).encoding
          : EncodingHelpers.initEncoding(action.chartType).encoding;
        const isCreatingPTQC = action?.chartType === 'vegasPivotTable';
        const fieldFormats = isCreatingPTQC
          ? { ...action.fieldFormats, ...ChartFormatHelpers.hideMeasureNamesHeader(encoding) }
          : action.fieldFormats;

        if (ChartFormatHelpers.isHighChartTypeEnabledForMuze(action.chartType)) {
          format = {
            ...format,
            useMuzeRenderer: true,
          };
        }

        let fieldColorsMap = {};
        // Since PTQC is initialized with a Measure Values field, we need to
        // create a Measure Values color map to get the expected palette colors
        if (isCreatingPTQC) {
          fieldColorsMap = ChartFormatHelpers.generateDefaultSequentialColor('@CON_$VALUES', sequentialPalette);
        }

        return this.chartClient
          .createChart({
            colorPaletteToken:
              action.chartType === 'bigValue'
                ? divergentPalette.token
                : isCreatingPTQC
                  ? sequentialPalette.token
                  : palette.token,
            switchViewToken: action.switchViewToken,
            chartType: action.chartType,
            encoding,
            isAutoUpdateDisabled: action.isAutoUpdateDisabled,
            fieldFormats,
            format,
            fieldColors: fieldColorsMap,
          })
          .pipe(
            map((chart) =>
              ChartSwitchActions.createNewChartSuccess({
                chart,
                switchViewToken: action.switchViewToken,
                queryToken: action.queryToken,
              })
            ),
            catchError((error) =>
              of(ChartSwitchActions.createNewChartFailure({ error, switchViewToken: action.switchViewToken }))
            )
          );
      })
    )
  );

  createNewTable$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChartSwitchActions.createNewTable),
      withLatestFrom(this.accountFacade.defaultCategoricalPalette$, this.accountFacade.defaultCategoricalChartFormat$),
      switchMap(([action, palette, format]) => {
        return this.chartClient
          .createChart({
            colorPaletteToken: palette.token,
            switchViewToken: action.switchViewToken,
            chartType: 'table',
            encoding: EncodingHelpers.initEncoding('table').encoding,
            format,
          })
          .pipe(
            map((chart) =>
              ChartSwitchActions.createNewTableSuccess({
                chart,
                switchViewToken: action.switchViewToken,
                queryToken: action.queryToken,
              })
            ),
            catchError((error) =>
              of(ChartSwitchActions.createNewTableFailure({ error, switchViewToken: action.switchViewToken }))
            )
          );
      })
    )
  );

  deleteChartFromBrowser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChartSwitchActions.deleteChartFromBrowser),
      switchMap((action) =>
        this.chartClient.deleteChart(action.token).pipe(
          map(() => ChartSwitchActions.deleteChartFromBrowserSuccess({ token: action.token })),
          catchError((error) => of(ChartSwitchActions.deleteChartFromBrowserFailure({ error, token: action.token })))
        )
      )
    )
  );

  cloneChartFromBrowser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChartSwitchActions.cloneChartFromBrowser),
      withLatestSnapshot(this.store, 'token'),
      switchMap(({ action, snapshot }) =>
        combineLatest([
          this.chartFacade.createSwitchViewTokenFromVizToken(action.token).pipe(filter(isPresent), first()),
          this.chartFacade.createQueryTokenFromVizToken(action.token).pipe(filter(isPresent), first()),
        ]).pipe(
          first(),
          map(([switchViewToken, queryToken]) => ({ action, snapshot, switchViewToken, queryToken }))
        )
      ),
      switchMap(({ action, snapshot, switchViewToken, queryToken }) =>
        this.chartClient.cloneChart({ ...snapshot, switchViewToken }).pipe(
          map((chart) =>
            ChartSwitchActions.cloneChartFromBrowserSuccess({
              chart,
              queryToken,
              switchViewToken,
            })
          ),
          catchError((error) =>
            of(
              ChartSwitchActions.cloneChartFromBrowserFailure({
                error,
                token: action.token,
              })
            )
          )
        )
      )
    )
  );

  cloneChartFromTab$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChartSwitchActions.cloneChartFromTab),
      withLatestSnapshot(this.store, 'token'),
      switchMap(({ action, snapshot }) =>
        combineLatest([
          this.chartFacade.createSwitchViewTokenFromVizToken(action.token).pipe(filter(isPresent), first()),
          this.chartFacade.createQueryTokenFromVizToken(action.token).pipe(filter(isPresent), first()),
        ]).pipe(
          first(),
          map(([switchViewToken, queryToken]) => ({ action, snapshot, switchViewToken, queryToken }))
        )
      ),
      switchMap(({ action, snapshot, switchViewToken, queryToken }) =>
        this.chartClient.cloneChart({ ...snapshot, switchViewToken }).pipe(
          map((chart) =>
            ChartSwitchActions.cloneChartFromTabSuccess({
              chart,
              queryToken,
              switchViewToken,
            })
          ),
          catchError((error) =>
            of(
              ChartSwitchActions.cloneChartFromTabFailure({
                error,
                token: action.token,
              })
            )
          )
        )
      )
    )
  );

  cloneChartFromBuilder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChartSwitchActions.cloneChartFromBuilder),
      withLatestSnapshot(this.store, 'token'),
      switchMap(({ action, snapshot }) =>
        combineLatest([
          this.chartFacade.createSwitchViewTokenFromVizToken(action.token).pipe(filter(isPresent), first()),
          this.chartFacade.createQueryTokenFromVizToken(action.token).pipe(filter(isPresent), first()),
        ]).pipe(
          first(),
          map(([switchViewToken, queryToken]) => ({ action, snapshot, switchViewToken, queryToken }))
        )
      ),
      switchMap(({ action, snapshot, switchViewToken, queryToken }) =>
        this.chartClient.cloneChart({ ...snapshot, switchViewToken }).pipe(
          map((chart) =>
            ChartSwitchActions.cloneChartFromBuilderSuccess({
              chart,
              queryToken,
              switchViewToken,
            })
          ),
          catchError((error) =>
            of(
              ChartSwitchActions.cloneChartFromBuilderFailure({
                error,
                token: action.token,
              })
            )
          )
        )
      )
    )
  );

  cloneChartFromDesigner$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChartSwitchActions.cloneChartFromDesigner),
      withLatestSnapshot(this.store, 'token'),
      switchMap(({ action, snapshot }) =>
        combineLatest([
          this.chartFacade.createSwitchViewTokenFromVizToken(action.token).pipe(filter(isPresent), first()),
          this.chartFacade.createQueryTokenFromVizToken(action.token).pipe(filter(isPresent), first()),
        ]).pipe(
          first(),
          map(([switchViewToken, queryToken]) => ({ action, snapshot, switchViewToken, queryToken }))
        )
      ),
      switchMap(({ action, snapshot, switchViewToken, queryToken }) =>
        this.chartClient.cloneChart({ ...snapshot, switchViewToken }).pipe(
          map((chart) =>
            ChartSwitchActions.cloneChartFromDesignerSuccess({
              chart,
              queryToken,
              switchViewToken,
            })
          ),
          catchError((error) =>
            of(
              ChartSwitchActions.cloneChartFromDesignerFailure({
                error,
                token: action.token,
              })
            )
          )
        )
      )
    )
  );

  /**
   * Using separate effect linked to Encoding update inside Flat table QC, in designer
   * view which resets paging offset to 0. This is handled separately here as paging
   * in designer view comes from PlanMetadata instead of ChartFeatureState.
   *
   * Also 'resetOffsetInDesigner' is different from 'setOffsetInDesigner' as the former
   * does not trigger a new switch plan, as the switch plan is anyways going to be triggered
   * later in the flow as it was initiated by an Encoding update.
   */
  updatePagingMetadata$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChartSwitchActions.updateDesignerChartEncoding),
      withLatestSnapshot(this.store, 'token'),
      filter(({ snapshot }) => snapshot.chartType === 'table'),
      map((data) => {
        return PlanMetadataActions.resetOffsetInDesigner({
          planMetadataId: makeVizDesignerPlanMetadataId(data.token),
          vizToken: data.token,
        });
      })
    )
  );

  /**
   * Update encoding for tables.
   *
   * This includes the default format fill. It's separated out from the non-table
   * encoding update below to more cleanly handle the conditionals and possible null
   * checking that would need to take place for typescript.
   */
  updateEditorTableEncodingTable$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChartSwitchActions.updateDesignerChartEncoding),
      withLatestSnapshot(this.store, 'token'),
      filter(({ snapshot }) => snapshot.chartType === 'table'),
      withLatestFrom(this.dataViewFacade.model$),
      map(([{ action, snapshot, queryToken, token }, model]) => {
        if (requiresFormatUpdateFromEncodingChange(snapshot.encoding, action.encoding)) {
          snapshot = {
            ...snapshot,
            ...getUpdatedTableFormat(model, queryToken, action.encoding, snapshot.fieldFormats, snapshot.format),
          };
        }

        return ChartSwitchActions.updateChart({
          token,
          chart: {
            ...snapshot,
            encoding: action.encoding,
          },
          sourceAction: action.type,
        });
      })
    )
  );

  addChartFromVizStorage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChartSwitchActions.addChartFromVizStorage),
      switchMap(({ snapshot, switchViewToken, queryToken }) =>
        this.chartClient.cloneChart({ ...snapshot, switchViewToken }, false).pipe(
          map((chart) =>
            ChartSwitchActions.cloneChartFromDesignerSuccess({
              chart,
              queryToken,
              switchViewToken,
            })
          ),
          catchError((error) =>
            of(
              ChartSwitchActions.cloneChartFromDesignerFailure({
                error,
                token: queryToken,
              })
            )
          )
        )
      )
    )
  );

  pasteAndReplaceChart$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChartSwitchActions.replaceFromVizStorage),
      switchMap(({ token, snapshot }) =>
        this.chartClient
          .updateChart({
            ...snapshot,
            format: { ...snapshot.format, useMuzeRenderer: snapshot.format.useMuzeRenderer ?? false },
            token,
          })
          .pipe(
            map((chart) =>
              ChartSwitchActions.updateChartSuccess({
                token: chart.token,
                chart,
                sourceAction: ChartSwitchActions.updateChartSuccess.type,
              })
            ),
            catchError((error) =>
              of(
                ChartSwitchActions.updateChartFailure({
                  error,
                  token,
                  sourceAction: ChartSwitchActions.updateChartFailure.type,
                })
              )
            )
          )
      )
    )
  );

  /**
   * Update encoding for non-tables.
   *
   * Only one of this effect or the effect above is run on encoding change.
   */
  updateEditorNonTableEncoding$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChartSwitchActions.updateDesignerChartEncoding, ChartSwitchActions.updateEncodingAndFormat),
      withLatestSnapshot(this.store, 'token'),
      withLatestFrom(
        this.dataViewFacade.model$,
        this.accountFacade.reportOwnerColorPalettes$,
        this.flamingoFacade.completedPlanRefs$
      ),
      filter(([{ snapshot }, model, palettes]) => snapshot.chartType !== 'table'),
      switchMap(([{ action, snapshot, token, queryToken }, model, palettes, planRefs]) => {
        return this.chartFacade.createRenderDataModel(token, planRefs[token]).pipe(
          map((renderModel) => ({
            action,
            token,
            snapshot,
            queryToken,
            renderModel,
            model,
            palettes,
          })),
          first()
        );
      }),

      map(({ action, token, snapshot, queryToken, renderModel, model, palettes }) => {
        let format = { ...snapshot.format, ...(action.format ?? {}) };
        let encoding = action.encoding;

        const useMuzeRenderer = ChartFormatHelpers.useMuzeRenderer(snapshot.chartType, format);
        let fieldFormats =
          useMuzeRenderer || snapshot.chartType === 'bigValue' ? (action.fieldFormats ?? snapshot.fieldFormats) : {};

        const rendererChanged =
          ChartFormatHelpers.useMuzeRenderer(snapshot.chartType, snapshot.format) !== useMuzeRenderer;
        const needsEncodingTranslation =
          !ChartTypes.isVegasChartType(snapshot.chartType) && (useMuzeRenderer || rendererChanged);

        // Start with the current field colors if this is using the Muze renderer.
        // For HighCharts, we can clear the field colors.
        let fieldColors = useMuzeRenderer ? (action.fieldColors ?? snapshot.fieldColors) : {};

        let colorPaletteToken = action.colorPaletteToken ?? snapshot.colorPaletteToken;

        if (needsEncodingTranslation) {
          const schemaModel = SwitchSchemaModel.make(model, queryToken as string);
          if (!schemaModel) {
            throw new Error('Unable to translate encoding.Schema model was not defined');
          }

          if (useMuzeRenderer) {
            encoding = ChartTranslator.translateHighChartsToMuze(snapshot.chartType, encoding, format, schemaModel);
            fieldFormats = ChartFormatHelpers.convertFormats(schemaModel, encoding, format, snapshot.chartType);
            if (rendererChanged) {
              // When the renderer changes from HighCharts -> Muze, we need to populate the latest fieldColors
              const colorDomain = renderModel ? renderModel.getColorDomain(snapshot.encoding, snapshot.format) : [];
              fieldColors = ChartFormatHelpers.convertColors(
                schemaModel,
                encoding,
                format,
                snapshot,
                palettes,
                colorDomain
              );
              // Clear the color map out of the HighCharts format object
              format = {
                ...format,
                colors: {
                  ...format.colors,
                  map: {},
                },
                xAxis: {
                  ...format.xAxis,
                  compact: ChartFormatHelpers.useCompactAxis(snapshot.chartType),
                },
              };
            }
          } else {
            encoding = ChartTranslator.translateMuzeToHighCharts(snapshot.chartType, encoding, format, schemaModel);
            if (rendererChanged) {
              // When the renderer changes from Muze -> HighCharts we need to populate the format color map.
              const { colors, colorPaletteToken: paletteToken } = ChartFormatHelpers.convertFieldColorsToFormats(
                schemaModel,
                encoding,
                snapshot.fieldColors,
                format,
                format.colors.custom
              );
              format = {
                ...format,
                colors: {
                  ...format.colors,
                  ...colors,
                },
              };
              colorPaletteToken = paletteToken ?? colorPaletteToken;
              // And clear the fieldColors map
              fieldColors = {};
            }
          }
        }
        // Unlike fields, if a group gets removed, it is never coming back. Creating a new group will
        // generate a new group id. To prevent orphaned groups in the FieldFormatMap, we will clean
        // up any group that has been removed with the encoding change.
        const removedGroups = SharedAxisHelpers.getRemovedGroups(snapshot.encoding, action.encoding);
        const cleanedFieldFormats = produce(fieldFormats, (draft) => {
          removedGroups.forEach((group) => {
            delete draft[FieldFormatHelpers.getFormatMapKeyByGroup(group)];
          });
        });

        // We can clear the QuickCharts color map for vegas chart or when the color field in QuickCharts changes.
        const newFormat = produce(format, (draft) => {
          if (useMuzeRenderer || isQuickChartColorChanged(snapshot.encoding, action.encoding)) {
            draft.colors.map = {};
          }
        });
        return ChartSwitchActions.updateChart({
          token,
          chart: {
            ...snapshot,
            encoding,
            format: newFormat,
            fieldFormats: cleanedFieldFormats,
            fieldColors,
            facetSizes: action.facetSizes ?? snapshot.facetSizes,
            colorPaletteToken,
          },
          sourceAction: action.type,
        });
      })
    )
  );

  deleteChartFromTabs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChartSwitchActions.deleteChartFromTabs),
      switchMap((action) =>
        this.chartClient.deleteChart(action.token).pipe(
          map(() => ChartSwitchActions.deleteChartFromTabsSuccess({ token: action.token })),
          catchError((error) => of(ChartSwitchActions.deleteChartFromTabsFailure({ error, token: action.token })))
        )
      )
    )
  );

  updateEditorTitle$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ChartSwitchActions.updateDesignerChartTitle),
      withLatestSnapshot(this.store, 'token'),
      map(({ action, snapshot, token }) =>
        ChartSwitchActions.updateChartNoEncoding({
          token,
          chart: {
            ...snapshot,
            title: action.title,
          },
          sourceAction: action.type,
        })
      )
    );
  });

  // TODO: updated this effect to follow the current pattern, but the facade implementation is
  // not yet done. Leaving this for those line items in the epic.
  updateEditorPalette$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChartSwitchActions.updateDesignerChartPalette),
      withLatestSnapshot(this.store, 'token'),
      map(({ token, action, snapshot }) =>
        ChartSwitchActions.updateChartNoEncoding({
          token,
          chart: {
            ...snapshot,
            colorPaletteToken: action.paletteToken,
          },
          sourceAction: action.type,
        })
      )
    )
  );

  updateChartFromSnapshot$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChartSwitchActions.updateChartFromSnapshot),
      withLatestSnapshot(this.store, 'token'),
      withLatestFrom(this.dataViewFacade.model$),
      map(([{ token, action, snapshot, queryToken }, model]) => {
        // requiresFormatUpdateFromEncodingChange
        // ---
        // Update formats using the metric's model which includes ALL attrs.
        // This fixes a super-setting issue when adding a field to the table
        // encoding and `dataViewFacade.model$` is a subset of the fields
        // present.. we need `format.flatTable.columns` to be in sync with
        // the entries in `fieldFormats` in order to render a table, and
        // `FlamingoHelpers.tableColumns` omits fields that cannot be compiled.
        // Ensuring we have the complete model fixes this
        model = action.model ?? model;
        if (model != null && snapshot.chartType === 'table') {
          const viewName = action.model ? model.name : queryToken;
          const formats = getUpdatedTableFormat(
            model,
            viewName,
            snapshot.encoding,
            snapshot.fieldFormats,
            snapshot.format
          );
          snapshot = {
            ...snapshot,
            ...formats,
          };
        }
        return ChartSwitchActions.updateChart({
          sourceAction: action.type,
          token,
          chart: {
            ...snapshot,
          },
        });
      })
    )
  );

  /**
   * The generic persistence effect.
   *
   * For the most part, all other effects should delegate to the `updateChart` or
   * `updateChartNoEncoding` actions. These actions are handled in each chart reducer
   * and persistence via the API is handled here in this effect.
   */
  updateChart$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChartSwitchActions.updateChart, ChartSwitchActions.updateChartNoEncoding),
      withLatestSnapshot(this.store, 'token'),
      switchMap(({ token, ...rest }) =>
        this.chartFacade.createEphemeralsFromVizToken(token).pipe(
          filter((ephemeral) => ephemeral === false),
          map(() => ({
            ...rest,
            token,
          }))
        )
      ),
      switchMap(({ action, snapshot }) =>
        this.chartClient
          .updateChart({
            ...snapshot,
            token: action.token,
          })
          .pipe(
            map((chart) =>
              ChartSwitchActions.updateChartSuccess({ token: chart.token, chart, sourceAction: action.sourceAction })
            ),
            catchError((error) =>
              of(
                ChartSwitchActions.updateChartFailure({ error, token: action.token, sourceAction: action.sourceAction })
              )
            )
          )
      )
    )
  );

  updateChartType$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChartSwitchActions.updateDesignerChartType),
      withLatestSnapshot(this.store, 'token'),
      withLatestFrom(
        this.dataViewFacade.model$,
        this.accountFacade.reportOwnerColorPalettes$,
        this.accountFacade.defaultCategoricalPalette$,
        this.accountFacade.defaultDivergentPalette$,
        this.accountFacade.defaultSequentialPalette$,
        this.flamingoFacade.completedPlanRefs$
      ),
      switchMap(
        ([
          { action, token, snapshot, queryToken },
          model,
          palettes,
          defaultCategoricalPalette,
          defaultDivergentPalette,
          defaultSequentialPalette,
          planRefs,
        ]) => {
          return this.chartFacade.createRenderDataModel(token, planRefs[token]).pipe(
            map((renderModel) => ({
              action,
              token,
              snapshot,
              queryToken,
              renderModel,
              model,
              palettes,
              defaultCategoricalPalette,
              defaultDivergentPalette,
              defaultSequentialPalette,
            })),
            first()
          );
        }
      ),
      map(
        ({
          action,
          token,
          snapshot,
          queryToken,
          renderModel,
          model,
          palettes,
          defaultCategoricalPalette,
          defaultDivergentPalette,
          defaultSequentialPalette,
        }) => {
          let format = snapshot.format;
          let encoding = snapshot.encoding;

          if (!palettes) {
            throw new Error('Schema model was not defined');
          }

          const paletteCompatible = isColorPaletteCompatible(
            action.chartType,
            palettes.find((p) => p.token === snapshot.colorPaletteToken)
          );

          format = {
            ...format,
            useMuzeRenderer: ChartFormatHelpers.isHighChartTypeEnabledForMuze(action.chartType),
          };

          // if the palette is incompatible, reset the color map
          if (!paletteCompatible) {
            const defaultPalettes = {
              categorical: defaultCategoricalPalette.token,
              divergent: defaultDivergentPalette.token,
            };

            const colorPaletteToken = getDefaultColorPaletteForType(action.chartType, defaultPalettes);
            format = produce(format, (draft) => {
              draft.colors = {
                theme: '',
                map: {}, // blank because if it's impossible to get a valid chart after an update type in this case
                custom: palettes.find((p) => p.token === colorPaletteToken)?.value.split(',') || [''],
              };
            });
            snapshot.colorPaletteToken = colorPaletteToken;
          }

          // make encoding changes
          const schemaModel = SwitchSchemaModel.make(model, queryToken as string);
          if (!schemaModel) {
            throw new Error('Schema model was not defined');
          }

          // TODO: Pending migration of chart-encoding-helpers.ts
          encoding = EncodingHelpers.convertEncoding(
            snapshot.chartType,
            action.chartType,
            snapshot.encoding,
            schemaModel,
            format
          ).encoding;

          const isSnapshotTypeMuze = ChartFormatHelpers.useMuzeRenderer(snapshot.chartType, snapshot.format);
          const isNewTypeMuze = ChartFormatHelpers.useMuzeRenderer(action.chartType, snapshot.format);
          //
          // Convert formats and colors.
          // If converting (Muze QC | VizExplorer) -> (Muze QC | VizExplorer): keep the current formats and colors. Copy MV to values.
          // If converting HighCharts QC -> (Muze QC | VizExplorer): convert formats and colors
          // If converting to PTQC: clear the header formats title and set to show false
          // If converting VizExplorer -> QC: clear the field formats and colors since we clear
          // the entire encoding.
          //

          const muzeToMuze = isSnapshotTypeMuze && isNewTypeMuze;
          const highChartsToMuze = !isSnapshotTypeMuze && isNewTypeMuze;
          const newChartIsPTQC = action?.chartType === 'vegasPivotTable';
          let fieldFormats = {};
          let fieldColors = {};

          if (muzeToMuze) {
            // The encoding transformation might remove Measure Values. Copy any MV formatting
            // to the fields in value. If MV wasn't removed, then the formats match between MV
            // and the fields which is easier to understand anyway.
            fieldFormats = ChartFormatHelpers.migrateFieldFormatsToVizExplorer(
              snapshot.encoding,
              snapshot.fieldFormats
            );
            fieldColors = snapshot.fieldColors;
          }

          if (highChartsToMuze) {
            const colorDomain = renderModel ? renderModel.getColorDomain(snapshot.encoding, snapshot.format) : [];
            fieldFormats = ChartFormatHelpers.convertFormats(schemaModel, encoding, format, snapshot.chartType);
            fieldColors = ChartFormatHelpers.convertColors(
              schemaModel,
              encoding,
              format,
              snapshot,
              palettes,
              colorDomain
            );
          }

          if (newChartIsPTQC) {
            fieldFormats = { ...fieldFormats, ...ChartFormatHelpers.hideMeasureNamesHeader(encoding) };
            // Since PTQC is initialized with a Measure Values field, we need to
            // create a Measure Values color map to get the expected palette colors
            fieldColors = ChartFormatHelpers.generateDefaultSequentialColor('@CON_$VALUES', defaultSequentialPalette);
          }

          // If this is a vegas chart, we can clear the QuickCharts color map.
          const newFormat = produce(format, (draft) => {
            if (isNewTypeMuze) {
              draft.colors.map = {};
            }
            draft.xAxis.compact = isNewTypeMuze && ChartFormatHelpers.useCompactAxis(snapshot.chartType);
          });

          return ChartSwitchActions.updateChart({
            token,
            chart: {
              ...snapshot,
              chartType: action.chartType,
              encoding,
              format: newFormat,
              fieldFormats,
              fieldColors,
            },
            sourceAction: action.type,
          });
        }
      )
    )
  );

  createChartRedo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChartSwitchActions.createChartRedo),
      withLatestSnapshot(this.store, 'token'),
      withLatestFrom(this.chartFacade.histories$),
      map(([{ token, snapshot }, histories]) => {
        return ChartSwitchActions.redo({
          token,
          snapshot: peekRedoStack(histories.get(token)) || snapshot,
        });
      })
    )
  );

  createChartUndo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChartSwitchActions.createChartUndo),
      withLatestSnapshot(this.store, 'token'),
      withLatestFrom(this.chartFacade.histories$),
      map(([{ token, snapshot }, histories]) => {
        return ChartSwitchActions.undo({
          token,
          snapshot: peekUndoStack(histories.get(token)) || snapshot,
        });
      })
    )
  );

  redo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChartSwitchActions.redo),
      withLatestFrom(this.chartFacade.histories$, (action, histories) => ({
        action,
        history: histories.get(action.token),
      })),
      switchMap(({ action: { token, snapshot, type }, history }) =>
        this.chartClient
          .updateChart({
            ...snapshot,
            token: token,
          })
          .pipe(
            map((chart) => ChartSwitchActions.updateChartSuccess({ token, chart, sourceAction: type })),
            catchError(() =>
              of(
                ChartSwitchActions.undoTemp({
                  token,
                  snapshot: peekUndoStack(history) || snapshot,
                })
              )
            )
          )
      )
    )
  );

  undo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChartSwitchActions.undo),
      withLatestFrom(this.chartFacade.histories$, (action, histories) => ({
        action,
        history: histories.get(action.token),
      })),
      switchMap(({ action: { token, snapshot, type }, history }) =>
        this.chartClient
          .updateChart({
            ...snapshot,
            token: token,
          })
          .pipe(
            map((chart) => ChartSwitchActions.updateChartSuccess({ token, chart, sourceAction: type })),
            catchError(() =>
              of(
                ChartSwitchActions.redoTemp({
                  token,
                  snapshot: peekRedoStack(history) || snapshot,
                })
              )
            )
          )
      )
    )
  );

  replaceChartHistory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChartSwitchActions.replaceChartHistory),
      withLatestSnapshot(this.store, 'token'),
      map(({ action, snapshot, token }) => {
        return ChartSwitchActions.updateChart({
          token,
          chart: {
            ...snapshot,
            encoding: action.encoding ?? snapshot.encoding,
            isAutoUpdateDisabled: action.isAutoUpdateDisabled ?? snapshot.isAutoUpdateDisabled,
          },
          sourceAction: action.type,
        });
      })
    )
  );

  // Handle update error.
  // TODO: temporarily we are just doing a console error. We will want to execute the same
  // behavior that exists in the current flow, which is rollback to the previous history state
  // and provide an error alert service. We don't have the history actions in place yet and
  // I don't believe we have an Angular alert service in place (though there might have been
  // work here by another team).
  updateChartFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChartSwitchActions.updateChartFailure),
      withLatestSnapshot(this.store, 'token'),
      withLatestFrom(this.chartFacade.histories$, (action, histories) => ({
        action,
        history: histories.get(action.token),
      })),
      tap(({ action }) => {
        const errorMessage = action.action.error;
        console.error('There was a problem saving your changes. ' + errorMessage);
      }),
      map(({ action, history }) => {
        return ChartSwitchActions.undoTemp({
          token: action.token,
          snapshot: peekUndoStack(history) || action.snapshot,
        });
      })
    )
  );

  // Clear Functions
  clearChart$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChartSwitchActions.clearChart),
      withLatestSnapshot(this.store, 'token'),
      withLatestFrom(this.accountFacade.defaultCategoricalChartFormat$),
      map(([{ token, action, snapshot }, format]) => {
        const encoding = EncodingHelpers.initEncoding(snapshot.chartType).encoding;
        return ChartSwitchActions.updateChart({
          token,
          chart: {
            ...snapshot,
            colorPaletteToken: format.colors.theme,
            encoding,
            facetSizes: {},
            fieldFormats: {},
            fieldColors: {},
            format: {
              ...format,
              useMuzeRenderer: ChartFormatHelpers.isHighChartTypeEnabledForMuze(snapshot.chartType),
            },
          },
          sourceAction: action.type,
        });
      })
    )
  );

  clearFilters$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChartSwitchActions.clearFilters),
      withLatestSnapshot(this.store, 'token'),
      map(({ action, token, snapshot }) => {
        const { encoding } = EncodingHelpers.clearFilters(snapshot.encoding);

        return ChartSwitchActions.updateChart({
          token,
          chart: {
            ...snapshot,
            encoding,
          },
          sourceAction: action.type,
        });
      })
    )
  );

  clearSorts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChartSwitchActions.clearSorts),
      withLatestSnapshot(this.store, 'token'),
      map(({ action, token, snapshot }) => {
        const { encoding } = EncodingHelpers.clearSorts(snapshot.encoding);
        return ChartSwitchActions.updateChart({
          token,
          chart: {
            ...snapshot,
            encoding,
          },
          sourceAction: action.type,
        });
      })
    )
  );

  clearFormatting$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChartSwitchActions.clearFormatting),
      withLatestSnapshot(this.store, 'token'),
      withLatestFrom(this.dataViewFacade.model$),
      map(([{ action, token, snapshot, queryToken }, model]) => {
        const isTable = snapshot.chartType === 'table';
        let isMuzeQuickChart = false;
        const vizExpFormat: Pick<ChartTypes.ViewVegas, 'fieldFormats'> = { fieldFormats: {} };
        // For QC, preserve the current renderer. This shouldn't change when the formats are cleared.
        const quickChartFormat = {
          format: { ...DEFAULT_CHART_FORMAT, useMuzeRenderer: snapshot.format.useMuzeRenderer },
        };
        quickChartFormat.format.colors = snapshot.format.colors;
        if (isTable) {
          vizExpFormat.fieldFormats = deepCopy(snapshot.fieldFormats);
          quickChartFormat.format.flatTable = {
            ...DEFAULT_CHART_FORMAT.flatTable,
            columns: snapshot.format.flatTable.columns,
          };
          Object.keys(vizExpFormat.fieldFormats).forEach((key: string) => {
            const colType = vizExpFormat.fieldFormats[key].paneFormat.default?.colType;
            if (colType) {
              vizExpFormat.fieldFormats[key] = getDefaultFieldFormat(colType);
            }
          });
        }
        if (
          !ChartTypes.isVegasChartType(snapshot.chartType) &&
          ChartFormatHelpers.useMuzeRenderer(snapshot.chartType, quickChartFormat.format)
        ) {
          isMuzeQuickChart = true;
          // For QuickCharts that are using the Muze renderer, we might need to update the
          // encoding depending on the nature of the format change.
          const schemaModel = SwitchSchemaModel.make(model, queryToken as string);
          if (
            schemaModel &&
            ChartTranslator.formatRequiresEncodingUpdate(
              snapshot.chartType,
              snapshot.format,
              quickChartFormat.format,
              schemaModel
            )
          ) {
            return ChartSwitchActions.updateDesignerChartEncoding({
              token,
              encoding: snapshot.encoding,
              format: quickChartFormat.format,
            });
          }
        }
        return ChartSwitchActions.updateChart({
          token,
          chart: {
            ...snapshot,
            // big value chart on quickcharts uses the fieldFormats obj to store the formats
            ...(ChartTypes.isVegasChartType(snapshot.chartType) || snapshot.chartType === 'bigValue'
              ? vizExpFormat
              : quickChartFormat),
            // When you asign a format to the columns using the toolbar on quickcharts it saves it on the fieldFormats obj.
            // When rendering QC with Muze, we need to clear any previously translated formats
            ...((isMuzeQuickChart || isTable) && vizExpFormat),
          },
          sourceAction: action.type,
        });
      })
    )
  );

  quickSort$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChartSwitchActions.quickSort),
      withLatestSnapshot(this.store, 'token'),
      map(({ token, action, snapshot }) => {
        const { encoding } = EncodingHelpers.quickSort(snapshot.encoding, action.descending, snapshot.chartType);
        return ChartSwitchActions.updateChart({
          token,
          chart: {
            ...snapshot,
            encoding,
          },
          sourceAction: action.type,
        });
      })
    )
  );

  removeSort$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChartSwitchActions.removeSort),
      withLatestSnapshot(this.store, 'token'),
      map(({ token, action: { formula, type }, snapshot }) => {
        const { encoding } = EncodingHelpers.removeSort(snapshot.encoding, formula);

        return ChartSwitchActions.updateChart({
          token,
          chart: {
            ...snapshot,
            encoding,
          },
          sourceAction: type,
        });
      })
    )
  );

  swapAxes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChartSwitchActions.swapAxes),
      withLatestSnapshot(this.store, 'token'),
      map(({ token, action, snapshot }) => {
        const chartType = EncodingHelpers.swapAxesChartType(snapshot.chartType);
        const encoding = EncodingHelpers.swapAxes(snapshot.encoding);
        return ChartSwitchActions.updateChart({
          token,
          chart: {
            ...snapshot,
            encoding,
            chartType,
            facetSizes: {}, // reset facet sizes
          },
          sourceAction: action.type,
        });
      })
    )
  );

  drillDown$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChartSwitchActions.drillDown),
      withLatestSnapshot(this.store, 'token'),
      withLatestFrom(this.dataViewFacade.model$),
      map(([{ action, snapshot, token, queryToken }, model]) => {
        const schemaModel = SwitchSchemaModel.make(model, queryToken);
        const chartType = ChartDrillDown.newChartType(snapshot.chartType, action.target);
        if (!schemaModel) {
          throw new Error('schemaModel is invalid');
        }
        const encoding = ChartDrillDown.encode(schemaModel, snapshot.encoding, action.target).encoding;
        return ChartSwitchActions.updateChart({
          token,
          chart: {
            ...snapshot,
            encoding,
            chartType,
          },
          sourceAction: action.type,
        });
      })
    )
  );

  changeHierarchy$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChartSwitchActions.changeHierarchy),
      withLatestSnapshot(this.store, 'token'),
      withLatestFrom(this.dataViewFacade.model$),
      filter(([{ action, snapshot, queryToken }, model]) => {
        const canChange = EncodingHelpers.canChangeHierarchy(snapshot.encoding, model, queryToken);
        return action.forward ? canChange.forward : canChange.back;
      }),
      map(([{ action, snapshot, token, queryToken }, model]) => {
        const modeView = Switch.Views.find(model, queryToken);
        if (!modeView) {
          throw new Error('modeView is invalid');
        }
        const encoding = EncodingHelpers.changeHierarchy(snapshot.encoding, modeView, action.forward).encoding;
        return ChartSwitchActions.updateChart({
          token,
          chart: {
            ...snapshot,
            encoding,
          },
          sourceAction: action.type,
        });
      })
    )
  );

  dynamicDrillDown$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChartSwitchActions.dynamicDrillDown),
      withLatestSnapshot(this.store, 'token'),
      withLatestFrom(this.dataViewFacade.model$),
      map(([{ action, snapshot, token, queryToken }, model]) => {
        const modeView = Switch.Views.find(model, queryToken);
        if (!modeView) {
          throw new Error(`ModeView is invalid for queryToken: ${queryToken}`);
        }
        const encoding = ChartDrillDown.updateEncodingForDynamicDrillDown(
          model,
          modeView,
          snapshot.encoding,
          action.target,
          action.targetFieldId,
          action.drillToField,
          action.dateTruncUnit
        );
        return ChartSwitchActions.updateChart({
          token,
          chart: {
            ...snapshot,
            encoding,
          },
          sourceAction: action.type,
        });
      })
    )
  );

  updateBigValueDescription$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChartSwitchActions.updateBigValueDescription),
      withLatestSnapshot(this.store, 'token'),
      map(({ token, action, snapshot }) =>
        ChartSwitchActions.updateChart({
          token,
          chart: {
            ...snapshot,
            format: {
              ...snapshot.format,
              bigValue: {
                ...snapshot.format.bigValue,
                description: action.description,
              },
            },
          },
          sourceAction: action.type,
        })
      )
    )
  );

  updateBigValueTrendLabel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChartSwitchActions.updateBigValueTrendLabel),
      withLatestSnapshot(this.store, 'token'),
      map(({ token, action, snapshot }) =>
        ChartSwitchActions.updateChart({
          token,
          chart: {
            ...snapshot,
            format: {
              ...snapshot.format,
              bigValue: {
                ...snapshot.format.bigValue,
                primaryTrendFormat: {
                  ...snapshot.format.bigValue.primaryTrendFormat,
                  label: action.label,
                },
              },
            },
          },
          sourceAction: action.type,
        })
      )
    )
  );

  // Update chart formatting
  updateFormat$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChartSwitchActions.updateFormat),
      withLatestSnapshot(this.store, 'token'),
      withLatestFrom(this.dataViewFacade.model$),
      map(([{ token, action, snapshot, queryToken }, model]) => {
        // This effect is only called for Quick Charts so we don't have to check if the chartType is 'vegas'
        const useMuzeRenderer = ChartFormatHelpers.useMuzeRenderer(snapshot.chartType, action.format);
        const rendererChanged =
          ChartFormatHelpers.useMuzeRenderer(snapshot.chartType, snapshot.format) !== useMuzeRenderer;

        const formatRequiresEncodingUpdate = () => {
          if (!useMuzeRenderer) {
            return false;
          }
          const schemaModel = SwitchSchemaModel.make(model, queryToken as string);
          if (!schemaModel) {
            throw new Error('Schema model was not defined');
          }
          return ChartTranslator.formatRequiresEncodingUpdate(
            snapshot.chartType,
            snapshot.format,
            action.format,
            schemaModel
          );
        };

        // We need to drive an encoding update if:
        //   - the renderer changes since HighCharts and Muze may require slightly different
        //     encodings. Changing the renderer is done exclusively through the format panel.
        //   - this format change requires an encoding update. This is usually associated with
        //     text labels.
        if (rendererChanged || formatRequiresEncodingUpdate()) {
          return ChartSwitchActions.updateDesignerChartEncoding({
            token,
            encoding: snapshot.encoding,
            format: action.format,
          });
        }
        // Otherwise, we just need to update either the format (HighCharts) or fieldFormats (Muze).
        const convertFormats = () => {
          const schemaModel = SwitchSchemaModel.make(model, queryToken as string);
          if (!schemaModel) {
            throw new Error('Schema model was not defined');
          }
          return ChartFormatHelpers.convertFormats(schemaModel, snapshot.encoding, action.format, snapshot.chartType);
        };

        const fieldFormats =
          useMuzeRenderer && !ChartFormatHelpers.isMuzeFormatChange(snapshot.format, action.format)
            ? convertFormats()
            : snapshot.fieldFormats;

        return ChartSwitchActions.updateChartNoEncoding({
          token,
          chart: {
            ...snapshot,
            fieldFormats,
            format: action.format,
          },
          sourceAction: action.type,
        });
      })
    )
  );

  updateFieldFormat$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChartSwitchActions.updateFieldFormat),
      withLatestSnapshot(this.store, 'token'),
      map(({ token, action, snapshot }) => {
        const fieldFormats = produce(snapshot.fieldFormats || {}, (draft) => {
          const fieldFormat = (draft[action.formula] ||= FieldFormatHelpers.getDefaultFieldFormat());
          fieldFormat.paneFormat = {
            ...fieldFormat.paneFormat,
            [action.pane]: {
              ...action.format,
            },
          };
        });

        return ChartSwitchActions.updateChartNoEncoding({
          token,
          chart: {
            ...snapshot,
            fieldFormats,
          },
          sourceAction: action.type,
        });
      })
    )
  );

  updateDesignerColors$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChartSwitchActions.updateDesignerColors),
      map((action) =>
        ChartSwitchActions.updateColors({
          ...action,
          planRefID: FLAMINGO_RESERVED_REFS.ChartDesigner,
        })
      )
    )
  );

  updateColors$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChartSwitchActions.updateColors),
      withLatestSnapshot(this.store, 'token'),
      withLatestFrom(this.flamingoFacade.completedPlanRefs$),
      switchMap(([{ action, token, snapshot }, planRefs]) => {
        return this.chartFacade.createRenderDataModel(token, planRefs[action.planRefID]).pipe(
          map((renderModel) => ({
            action,
            token,
            snapshot,
            renderModel,
          })),
          first()
        );
      }),
      map(({ action, token, snapshot, renderModel }) => {
        const format = produce(snapshot.format, (format) => {
          format.colors.custom = action.palette;

          if (!renderModel) {
            return;
          }

          format.colors.map = generateColorMapping(renderModel, snapshot.encoding, format);
        });

        const useMuzeRenderer = ChartFormatHelpers.useMuzeRenderer(snapshot.chartType, snapshot.format);
        const fieldColors = useMuzeRenderer
          ? // Regenerate a new color mapping since the palette length may have changed.
            produce(snapshot.fieldColors, (colors) => {
              Object.keys(colors).forEach((key) => {
                colors[key].colorPaletteToken = action.colorPaletteToken;
                colors[key].palette = action.palette;
                colors[key].map = Object.keys(colors[key].map).reduce(
                  (records, value, index) => {
                    records[value] = index % action.palette.length;
                    return records;
                  },
                  {} as Record<string, number>
                );
              });
            })
          : snapshot.fieldColors;

        return ChartSwitchActions.updateChartNoEncoding({
          token,
          chart: {
            ...snapshot,
            format,
            colorPaletteToken: action.colorPaletteToken,
            fieldColors,
          },
          sourceAction: action.type,
        });
      })
    )
  );

  // Updates facet size map.
  updateFacetSizes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChartSwitchActions.updateFacetSizes),
      withLatestSnapshot(this.store, 'token'),
      map(({ action, snapshot, token }) => {
        const facetSizes = FacetSizeHelpers.mergeFacetSizeMaps(snapshot.facetSizes, action.facetSizes);
        return ChartSwitchActions.updateChartNoEncoding({
          token,
          chart: {
            ...snapshot,
            facetSizes,
          },
          sourceAction: action.type,
        });
      })
    )
  );

  // Updates chart encoding and format in flat table.
  updateEncodingAndFormatInFlatTable$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChartSwitchActions.updateEncodingAndFormat),
      withLatestSnapshot(this.store, 'token'),
      filter(({ snapshot }) => snapshot.chartType === 'table'),
      map(({ action, token, snapshot }) => {
        return ChartSwitchActions.updateChart({
          token,
          chart: {
            ...snapshot,
            encoding: action.encoding,
            format: { ...snapshot.format, ...(action.format ?? {}) },
          },
          sourceAction: action.type,
        });
      })
    )
  );

  updateIsAutoUpdateDisabled$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChartSwitchActions.updateIsAutoUpdateDisabled),
      withLatestSnapshot(this.store, 'token'),
      map(({ action, token, snapshot }) => {
        return ChartSwitchActions.updateChart({
          token,
          chart: {
            ...snapshot,
            isAutoUpdateDisabled: action.isAutoUpdateDisabled,
          },
          sourceAction: action.type,
        });
      })
    )
  );

  // This Effect handles adding to the number of unapplied changes in Manual mode or clearing the unapplied changes
  // when we switch to Auto-update mode or the user manually applies the changes.
  //
  // This Effect is triggered off a long list of Actions which can modify the encoding, formats or colors in a
  // visualization.  See 'updateEncodingActions' and 'updateFormatOrColorActions' in chart-helpers for the complete list.
  //
  updateUnappliedChangesOnChartChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(...updateEncodingActions, ...updateFormatOrColorActions),
      withLatestSnapshot(this.store, 'token'),
      switchMap(({ action, snapshot }) =>
        combineLatest([
          this.chartFacade.createManuallyApplyChangesFromVizToken(action.token).pipe(filter(isPresent), first()),
          this.chartFacade.createUnappliedChangesFromVizToken(action.token).pipe(filter(isPresent), first()),
        ]).pipe(
          first(),
          map(([manuallyApplyChanges, unappliedChanges]) => ({
            action,
            snapshot,
            manuallyApplyChanges,
            unappliedChanges,
          }))
        )
      ),
      filter(({ snapshot, manuallyApplyChanges }) => snapshot.isAutoUpdateDisabled && !manuallyApplyChanges),
      map(({ action, unappliedChanges }) => {
        // A format change only increments the count if there has already been an encoding change. This allows the
        // user to make format/color changes live in Manual mode if the encoding has not changed. If we find that
        // there are some format changes that are expensive, we may want to change this behavior.
        //
        // The unappliedChanges count can be positive or negative. It can be negative if the changes were incurred
        // by undo (which always decrements). EX: If the user applies the changes and then hits undo, the
        // unapplied changes will be -1 (the UI always sees the ABS). If the user makes a regular change,
        // then we want the unapplied changes to be -2 (not 0) since they are not back to the original state.
        const unapplied =
          unappliedChanges === 0 && isUpdateFormatOrColorAction(action.type)
            ? 0
            : unappliedChanges + Math.sign(unappliedChanges) || 1;
        return ChartSwitchActions.setUnappliedChangesCount({
          token: action.token,
          count: unapplied,
        });
      })
    )
  );

  // This Effect handles updating the 'unapplied changes' count in Manual mode when there is an undo or redo.
  // Since not all Actions that are undo-able affect the 'unapplied changes' count, we filter this Effect
  // based on the type of the last Action in the undo/redo stack.
  updateUnappliedChangesOnUndoRedo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChartSwitchActions.undo, ChartSwitchActions.redo),
      withLatestSnapshot(this.store, 'token'),
      withLatestFrom(this.chartFacade.histories$, ({ action, snapshot }, histories) => ({
        action,
        snapshot,
        lastAction:
          action.type === ChartSwitchActions.undo.type
            ? histories.get(action.token)?.redoStack[0]?.action
            : histories.get(action.token)?.undoStack[0]?.action,
      })),
      filter(
        ({ lastAction }) =>
          isUpdateChartAction(lastAction) || lastAction === ChartSwitchActions.updateIsAutoUpdateDisabled.type
      ),
      switchMap(({ action, snapshot, lastAction }) =>
        combineLatest([
          this.chartFacade.createManuallyApplyChangesFromVizToken(action.token).pipe(filter(isPresent), first()),
          this.chartFacade.createUnappliedChangesFromVizToken(action.token).pipe(filter(isPresent), first()),
        ]).pipe(
          first(),
          map(([manuallyApplyChanges, unappliedChanges]) => ({
            action,
            snapshot,
            lastAction,
            manuallyApplyChanges,
            unappliedChanges,
          }))
        )
      ),
      map(({ action, snapshot, lastAction, manuallyApplyChanges, unappliedChanges }) => {
        const isFormatOrColorChange = isUpdateFormatOrColorAction(lastAction);
        const unappliedChangeCount = (() => {
          if (!snapshot.isAutoUpdateDisabled || manuallyApplyChanges) {
            // In auto-update mode or when the user manually applied the changes,
            // the unapplied count should be reset to 0.
            return 0;
          }
          if (isFormatOrColorChange && unappliedChanges === 0) {
            // When the change is format/color related and we are back to 0 changes,
            // don't change the count.
            return unappliedChanges;
          }
          // Otherwise, increment/decrement based on the type
          return action.type === ChartSwitchActions.undo.type ? --unappliedChanges : ++unappliedChanges;
        })();

        return ChartSwitchActions.setUnappliedChangesCount({
          token: action.token,
          count: unappliedChangeCount,
        });
      })
    )
  );

  updateCaption$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChartSwitchActions.updateCaption),
      withLatestSnapshot(this.store, 'token'),
      map(({ action, token, snapshot }) => {
        return ChartSwitchActions.updateChart({
          token,
          chart: {
            ...snapshot,
            caption: action.caption,
          },
          sourceAction: action.type,
        });
      })
    )
  );

  constructor(
    private actions$: Actions,
    private store: Store<DomainAppState>,
    private dataViewFacade: DataViewFacade,
    private accountFacade: AccountFacade,
    private flamingoFacade: FlamingoFacade,
    private chartClient: ChartClientService,
    private chartFacade: ChartFacade
  ) {}
}
